import { NetworkStatusService } from "./network-status.service";
import { Observable, of, Subject, zip } from "rxjs";
import { switchMap, switchMapTo } from "rxjs/operators";
import { Inject, Injectable } from "@angular/core";
import { ApplicationError } from "../_errors/application-error";
import { SubjectLocation, SubjectModel } from "../_objects/subject-model";
import {
  BROWSER_TYPE,
  DEVICE_TYPE,
  IMAGE_FILETYPE,
  IMAGE_TYPE,
  INSPECTION_REPORT_STATUS_CODE,
  SCREEN_SIZE,
  SCREEN_SIZE_BREAKPOINT
} from "../app.constants";
import { MarkItem } from "../_objects/mark";
import { IVisolFilePickerService, VISOL_FILE_PICKER_SERVICE } from "../common-dialog/file-manager/visol-file-picker.service";

@Injectable()
export class UtilsService {
  private isUpdate$: Subject<string>;
  public isUpdate: Observable<string>;

  constructor(
    private networkStatusService: NetworkStatusService,
    @Inject(VISOL_FILE_PICKER_SERVICE) private visolFilePicker: IVisolFilePickerService
  ) {
    this.isUpdate$ = new Subject();
    this.isUpdate = this.isUpdate$.asObservable();
  }

  private getFileExtension(filename: string): string | undefined {
    const parts = filename.split(".");
    if (parts.length > 1) {
      return parts.pop(); // Returns the last part after the last dot
    }
    return undefined; // No extension found
  }

  download(blob: Blob, fileName: string, reportId: string = null): void {
    if (!blob || !blob.type) {
      throw new ApplicationError("Blob is invalid.");
    }

    if (!fileName) {
      throw new ApplicationError("Filename is invalid.");
    }

    if (this.networkStatusService.useApplicationFileManager()) {
      this.showFileManager(fileName, blob);
    } else {
      const browserName = this.getBrowserName();

      if (browserName === BROWSER_TYPE.SAFARI) {
        if (reportId === null) {
          reportId = "0";
        }
        window.open(window.URL.createObjectURL(blob), "child" + reportId);
      } else {
        const a = document.createElement("a");
        a.download = fileName;
        a.href = window.URL.createObjectURL(blob);
        a.click();
      }
    }
  }

  showFileManager(suggestedFileName: string, blob: Blob) {
    this.visolFilePicker.exportFile({ content: blob, filename: suggestedFileName });
  }

  getLocations(subjectModel: SubjectModel, selectedLocation?: SubjectLocation, filterLocationType?: string): SubjectLocation[] {
    const locations: SubjectLocation[] = [];

    subjectModel.locations.forEach((location) => {
      if (
        (!selectedLocation || location.identifier === selectedLocation.identifier) &&
        (!filterLocationType || !(filterLocationType === IMAGE_TYPE.DATAPLATE) || location.isDataPlate)
      ) {
        this.searchTermLocs(location, locations);
      }
      this.searchLocationInTree(location, selectedLocation, locations);
    });
    return locations;
  }

  private searchLocationInTree(location: SubjectLocation, selectedLocation: SubjectLocation, locations: SubjectLocation[]) {
    location.children.forEach((childLoc) => {
      if (selectedLocation?.identifier === childLoc.identifier) {
        locations.push(childLoc);
      }
      this.searchLocationInTree(childLoc, selectedLocation, locations);
    });
  }

  private searchTermLocs(targetLoc: SubjectLocation, locations: SubjectLocation[]) {
    if (0 < targetLoc.children.length) {
      targetLoc.children.forEach((child) => {
        this.searchTermLocs(child, locations);
      });
    } else {
      locations.push(targetLoc);
    }
  }

  // browser nameを判定
  getBrowserName(): string {
    const ua: string = window.navigator.userAgent.toLowerCase();

    if (ua.indexOf("edge") !== -1) {
      return BROWSER_TYPE.EDGE;
    } else if (ua.indexOf("chrome") !== -1) {
      return BROWSER_TYPE.CHROME;
    } else if (ua.indexOf("crios") !== -1) {
      return BROWSER_TYPE.CHROME;
    } else if (ua.indexOf("safari") !== -1) {
      return BROWSER_TYPE.SAFARI;
    } else if (ua.indexOf("firefox") !== -1) {
      return BROWSER_TYPE.FIREFOX;
    } else if (ua.indexOf("opera") !== -1) {
      return BROWSER_TYPE.OPERA;
    } else {
      return BROWSER_TYPE.OTHER;
    }
  }

  // device nameを判定
  getDeviceName(): string {
    const ua: string = window.navigator.userAgent;

    if (ua.indexOf("iPad") !== -1) {
      return DEVICE_TYPE.IPAD;
    } else if (ua.indexOf("iPhone") !== -1) {
      return DEVICE_TYPE.IPHONE;
    } else if (ua.indexOf("android") !== -1) {
      return DEVICE_TYPE.ANDROID;
    } else {
      return DEVICE_TYPE.OTHER;
    }
  }

  isSafari(browserName): boolean {
    return browserName === BROWSER_TYPE.SAFARI;
  }

  isEdge(browserName): boolean {
    return browserName === BROWSER_TYPE.EDGE;
  }

  isReportStatusEditing(reportStatus): boolean {
    return reportStatus === INSPECTION_REPORT_STATUS_CODE.EDITING;
  }

  isReportStatusFailed(reportStatus): boolean {
    return reportStatus === INSPECTION_REPORT_STATUS_CODE.FAILED;
  }

  isDeviceNameIpad(deviceName): boolean {
    return deviceName === DEVICE_TYPE.IPAD;
  }

  isNullOrUndefined(val): boolean {
    return val === undefined || val === null;
  }

  updateHeaderName() {
    this.isUpdate$.next("Updated");
  }

  async getFileFromUrl(url: string, fileName: string, type?: string, callback?: () => void): Promise<File> {
    const blob = await fetch(url).then((response) => {
      if (callback) {
        callback();
      }
      return response.blob();
    });

    if (!type) {
      type = blob.type;
    }

    return new File([blob], fileName, { type: type });
  }

  /**
   * BASE64文字列からFile形式に変換する
   * @param  {string} base64
   * @param  {string} fileName
   * @param  {string} mimeType
   * @returns File
   */
  convertFileFromBase64Str(base64: string, fileName: string, mimeType: string): File {
    // base64のデコード
    const bin = atob(base64.replace(/^.*,/, ""));

    // バイナリデータ化
    const buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
    }

    return new File([buffer.buffer], fileName, { type: mimeType });
  }

  fileToBase64(file: File) {
    if (!file) {
      return of(null as string).toPromise();
    }

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener(
        "load",
        function () {
          resolve(reader.result as string);
        },
        false
      );

      reader.onerror = (error) => {
        console.log(error);
        return reject(this);
      };
      reader.readAsDataURL(file);
    });
  }

  getImageElementFromFile(file: File) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const fr = new FileReader();

      fr.onload = function () {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = fr.result as string;
      };

      fr.readAsDataURL(file);
    });
  }

  /**
   * @param array ex)[1, 2, 3, 4, ... , 100];
   * @param num ex)10
   * @returns
   * ex)
   * [
   *  [1, 2, 3, 4, ... , 10],
   *  [11, 12, 13, 14, ... , 20],
   *  ...,
   *  [91, 92, 93, 94, ... , 100]
   * ]
   */
  chainObservables<T>(array: Observable<T>[], num: number) {
    const parsed = this.parseArray(array, num);
    const zipChain = parsed.map((list) =>
      zip(...list).pipe(
        switchMap((result) => {
          return of(result);
        })
      )
    );
    return this.pipeChain(zipChain);
  }

  parseArray<T>(array: T[], num: number): T[][] {
    return array.reduce((arr, v, i) => (i % num ? [...arr.slice(0, arr.length - 1), [...arr[arr.length - 1], v]] : [...arr, [v]]), []);
  }

  pipeChain<T>(array: Observable<T[]>[]) {
    let result: Observable<T[]> = null;

    array.forEach((e) => (result = result ? result.pipe(switchMapTo(e)) : e));

    return result;
  }

  chainToPromises<T>(array: Promise<T>[], num: number) {
    const parsed = this.parseArray(array, num);
    const chain = parsed.map((list) => this.thenChain(list));
    return Promise.all(chain);
  }

  thenChain<T>(array: Promise<T>[]) {
    let result: Promise<T> = null;

    array.forEach((e) => (result = result ? result.then((_) => e) : e));

    return result;
  }

  CombiningRegExp(array: string[]): RegExp {
    return new RegExp(array.join(""), "i");
  }

  /**
   * 拡張子を変換する。
   *
   * @param filePath ファイルパス。
   * @param extension 新しい拡張子。
   * @returns 拡張子を変換したファイルパス。
   */
  public changeExtension(filePath: string, extension: string): string {
    let result: string = filePath;

    const delimitedFilePath: Array<string> = filePath.split(/\\|\//);
    const fileName: string = delimitedFilePath[delimitedFilePath.length - 1];
    let originalFileExtension: string = "";

    if (fileName.split(".").length > 1) {
      originalFileExtension = fileName.split(".").pop();
    }

    let replaceString: string = extension;
    if (extension != "" && !extension.startsWith(".")) {
      replaceString = "." + extension;
    }

    if (fileName.split(".").length > 1) {
      result = filePath.replace(new RegExp("\\." + originalFileExtension + "$", "i"), replaceString);
    } else {
      result = filePath + replaceString;
    }

    return result;
  }

  /**
   * ファイル名を取得する。
   *
   * @param filePath ファイルパス。
   * @returns ファイル名。
   */
  public getFileName(filePath: string): string {
    const delimitedFilePath: Array<string> = filePath.split(/\\|\//);
    return delimitedFilePath[delimitedFilePath.length - 1];
  }

  /**
   * 拡張子を取得する。
   *
   * @param filePath ファイルパス。
   * @returns 拡張子。
   */
  public getExtension(filePath: string): string {
    let result: string = "";

    const fileName: string = this.getFileName(filePath);

    if (fileName.split(".").length > 1) {
      result = fileName.split(".").pop();
    }

    return result;
  }

  /**
   * 拡張子から MimeType を取得する。
   *
   * @param filePath ファイルパス。
   * @returns MimeType。該当する MimeType が無い場合は空文字を返す。
   */
  public getMimeType(filePath: string): string {
    /** 対象の拡張子、 MimeType の一覧 */
    const mimeTypes: Map<string, string> = new Map<string, string>([
      [IMAGE_FILETYPE.EXTENSION_JPG, IMAGE_FILETYPE.JPEG],
      [IMAGE_FILETYPE.EXTENSION_JPEG, IMAGE_FILETYPE.JPEG],
      [IMAGE_FILETYPE.EXTENSION_TIF, IMAGE_FILETYPE.TIF],
      [IMAGE_FILETYPE.EXTENSION_TIFF, IMAGE_FILETYPE.TIFF],
      [IMAGE_FILETYPE.EXTENSION_HEIC, IMAGE_FILETYPE.HEIC],
      [IMAGE_FILETYPE.EXTENSION_HEIF, IMAGE_FILETYPE.HEIF],
      [IMAGE_FILETYPE.EXTENSION_PNG, IMAGE_FILETYPE.PNG],
      [IMAGE_FILETYPE.EXTENSION_BMP, IMAGE_FILETYPE.BMP],
      [IMAGE_FILETYPE.EXTENSION_MP4, IMAGE_FILETYPE.MP4]
    ]);

    let result: string = "";

    const extension: string = this.getExtension(filePath.toLowerCase());

    if (mimeTypes.has(extension)) {
      result = mimeTypes.get(extension);
    } else {
      result = "";
    }

    return result;
  }

  public trimAllSpace(str: string) {
    return !str ? "" : str.replace(/\s+/g, "");
  }

  /**
   * Breakpointに応じたScreenSizeを取得する。
   * @returns ScreenSize
   */
  getScreenSize() {
    const width = window.innerWidth;
    switch (typeof width === "number") {
      case width <= SCREEN_SIZE_BREAKPOINT.COMPACT_MAX:
        return SCREEN_SIZE.COMPACT;
      case width >= SCREEN_SIZE_BREAKPOINT.MEDIUM_MIN && width <= SCREEN_SIZE_BREAKPOINT.MEDIUM_MAX:
        return SCREEN_SIZE.MEDIUM;
      case width >= SCREEN_SIZE_BREAKPOINT.EXPANDED_MIN && width <= SCREEN_SIZE_BREAKPOINT.EXPANDED_MAX:
        return SCREEN_SIZE.EXPANDED;
      case width >= SCREEN_SIZE_BREAKPOINT.LARGE_MIN && width <= SCREEN_SIZE_BREAKPOINT.LARGE_MAX:
        return SCREEN_SIZE.LARGE;
      case width >= SCREEN_SIZE_BREAKPOINT.EXTRA_LARGE_MIN:
        return SCREEN_SIZE.EXTRA_LARGE;
    }
  }

  getMarkFromFilenameSuffix(fileNameWithoutExtension: string, marks: MarkItem[]) {
    const marksWithPositiveIds = marks.filter((x) => x.identifier > 0);
    if (!marksWithPositiveIds.find((_) => fileNameWithoutExtension.endsWith("_" + _.code))) {
      return "";
    } else {
      return "_" + marksWithPositiveIds.find((_) => fileNameWithoutExtension.endsWith("_" + _.code)).code;
    }
  }

  getFileNameWithoutMark(fileName: string, marks: MarkItem[]) {
    const marksWithPositiveIds = marks.filter((x) => x.identifier > 0);
    if (marksWithPositiveIds.find((_) => fileName.endsWith("_" + _.code))) {
      return fileName.slice(0, -2);
    }
    return fileName;
  }

  /**
   * 拡張子を除去したファイルパスを取得する。
   *
   * @param filePath ファイルパス。
   * @returns 拡張子を除去したファイルパス。
   */
  getFilePathWithoutExtension(filePath: string): string {
    let result: string = filePath;

    const delimitedFilePath: Array<string> = filePath.split(/\\|\//);
    const fileName: string = delimitedFilePath[delimitedFilePath.length - 1];
    let extension: string = "";

    if (fileName.split(".").length > 1) {
      extension = fileName.split(".").pop();
    }

    if (fileName.split(".").length > 1) {
      result = filePath.replace(new RegExp("\\." + extension + "$", "i"), "");
    }

    return result;
  }

  getFileNameWithoutExtension(filePath: string): string {
    const fileName = filePath.split(/[/\\]/).pop() || "";

    const lastDotIndex = fileName.lastIndexOf(".");
    return lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
  }

  removeMimeType(fileName: string): string {
    return fileName.split(".")[0];
  }
}
